/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Descriptions, Spin } from 'antd';
import {
  getTenantPowerSummary,
  tenantPowerSummaryStart,
} from '@/services/athena';

/**
 * POWER 总结（商家概况 / 服务情况 / 会员画像）
 * ==================================================================
 * 从旧「商家档案-概览」tab（detail/components/overview）原样抽出来的，
 * 生成机制与口径不变：
 *   1. 先 POST /athena/tenantPowerSummaryStart 触发后台生成；
 *   2. 再轮询 /athena/getTenantPowerSummary，直到 statue == 2（源字段名就是
 *      `statue`，拼写如此，不是笔误）或重试 20 次为止，间隔 5s。
 *
 * 抽出来是为了让「商家档案 → POWER 总结」二级 tab 与旧概览共用同一份实现，
 * 不再各写一套轮询。
 *
 * @param tenantId 商家ID（必传；为空时不发请求）
 */
const POLL_INTERVAL_MS = 5000;
const MAX_RETRY = 20;
/** 源接口完成态：statue == 2 表示生成完成 */
const DONE_STATUS = 2;

const PowerSummary = (props) => {
  const { tenantId } = props;
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId) return undefined;

    /* 轮询生命周期归 effect 所有：cancelled 防止卸载/切商家后 setState，
       timer 在清理函数里统一清掉（旧实现用 ref 存 timer+重试计数，
       卸载时只清了 timer，这里一并收干净）。 */
    let cancelled = false;
    let timer = null;
    let retry = 0;

    /* 用函数声明（会提升）避免“先定义后引用”的顺序依赖 */
    function scheduleRetry() {
      if (cancelled) return;
      if (retry >= MAX_RETRY) {
        // 重试耗尽：停止 loading，页面显示 '-'，不再无限转圈
        setLoading(false);
        return;
      }
      retry += 1;
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    }

    function poll() {
      if (cancelled) return;
      setLoading(true);
      getTenantPowerSummary({ requestData: { tenantId } })
        .then((res) => {
          if (cancelled) return;
          if (res.code === '00000') {
            const next = res.data || {};
            setData(next);
            if (String(next.statue) === String(DONE_STATUS)) {
              setLoading(false);
              return;
            }
          }
          // 接口报错或尚未生成完，都继续重试（与旧实现一致）
          scheduleRetry();
        })
        .catch(() => scheduleRetry());
    }

    tenantPowerSummaryStart({ requestData: { tenantId } })
      .then(() => poll())
      .catch(() => poll());

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
  }, [tenantId]);

  return (
    <Spin spinning={loading}>
      <Descriptions
        bordered
        column={1}
        labelStyle={{ width: 140 }}
        contentStyle={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
      >
        <Descriptions.Item label="商家概况">
          {data.tenantOverview || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="服务情况">
          {data.tenantService || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="会员画像">
          {data.memberProfile || '-'}
        </Descriptions.Item>
      </Descriptions>
    </Spin>
  );
};

export default PowerSummary;
